<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast, wait, formatDate } from "$app/utils"
  import Sortable from "sortablejs"
  import {
    Info,
    SquarePlus,
    CircleEllipsis,
    Copy,
    Trash2,
    Square,
    SquareCheck
  } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import NewTask from "./NewTask.svelte"
  import Expanded from "$app/Pages/Plans/Goals/Expanded.svelte"
  import Modal from "$app/components/Modal.svelte"
  import InlineInput from "$app/components/InlineInput.svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import Task from "./Task.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Dropdown from "$app/components/Dropdown.svelte"
  import QuarterlyDuplicate from "./QuarterlyDuplicate.svelte"

  interface Props {
    goal: GoalType
    period?: string
    allowDuplicate?: boolean
    showMoveToParent?: boolean
    allowGoalSelect?: boolean
    allowTaskSelect?: boolean
    pos?: number
    onopenGoalModal: (goal: GoalType) => void
    onopenTaskModal: (task: TaskType) => void
  }

  let {
    goal = $bindable(),
    period = "prev",
    allowDuplicate = true,
    showMoveToParent = false,
    allowGoalSelect = true,
    allowTaskSelect = true,
    pos = 1,
    onopenGoalModal,
    onopenTaskModal
  }: Props = $props()

  let sortableSection: HTMLElement = $state()

  const dates = appStore.dates
  const channel_id = appStore.channel_id

  let openGoalDropdown = $state(false)
  let openDuplicateGoal = $state(false)
  let toggleTasks = $state(false)
  let expand = $state(false)

  async function deleteGoal(): Promise<void> {
    openGoalDropdown = false
    goal._selected = true

    await wait(10)
    if (!confirm(`Are you sure you want to delete goal "${goal.name}"?`)) {
      goal._selected = false
      return
    }
    await wait(50)

    try {
      await xhr.delete(
        `/plans/${goal.plan_id}/goals/${goal.id}?channel=${channel_id}`
      )
      appStore.goals = appStore.goals.filter((g) => g.id !== goal.id)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error deleting the goal", "error")
    } finally {
      goal._selected = true
    }
  }

  function updateGoal(data: GoalType): void {
    const idx = appStore.goals.findIndex((g) => g.id === goal.id)
    appStore.goals[idx] = { ...goal, ...data }
  }

  async function duplicateGoal(): Promise<void> {}

  function setToggleTasks(): void {
    toggleTasks = !toggleTasks
    appStore.tasks
      .filter((t) => t.goal_id === goal.id)
      .forEach((t) => (t._selected = toggleTasks))
    appStore.tasks = appStore.tasks
  }

  function updateGoalSelected(e: MouseEvent): void {
    const idx = appStore.goals.findIndex((g) => g.id === goal.id)

    const target = e.target as HTMLInputElement
    appStore.goals[idx]._selected = target.checked
    if (!target.checked) {
      goal.toggleTasks = false
      appStore.tasks
        .filter((t) => t.goal_id === goal.id)
        .forEach((t) => (t._selected = false))
    }
    appStore.goals = appStore.goals
  }

  function moveToParent(): void {
    alert("move to parent")
  }

  async function updateTasksOrder(newTasks: TaskTypeE[]): Promise<void> {
    try {
      const resp = await xhr.post(
        `/plans/${goal.plan_id}/tasks/sort_by_parent.json`,
        {
          goal_id: goal.id,
          tasks: newTasks.map((t) => t.id),
          channel: channel_id
        }
      )
    } catch (e) {
      const tmpTasks = [...appStore.tasks]
      appStore.tasks = []
      await wait(50)
      appStore.tasks = [...tmpTasks]
      toast("There was an error updating the tasks order", "error")
    }
  }

  let sort: any = $state()
  /**
   * Creates sortable for tasks
   */
  function setSortable(): void {
    if (!sortableSection) return
    sort = Sortable.create(sortableSection, {
      animation: 200,
      group: `QuarterlyGoal-${goal.id}`,
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        sort.options.sort = false
        // Move from another container
        // new sort order
        if (e.newIndex !== e.oldIndex) {
          let newTasks: TaskTypeE[] = []
          e.from.querySelectorAll(".task").forEach((el: HTMLElement) => {
            const id = parseInt(el.dataset.id)
            const cTask = appStore.tasks.find((t) => id === t.id)
            newTasks.push(cTask)
          })

          sort.options.sort = true

          await updateTasksOrder(newTasks)
        }
      },
      onStart: (evt: EventSortType) => {
        appStore.draggedElement = {
          ...appStore.draggedElement,
          type: "QuarterlyGoal",
          id: parseInt(evt.item.dataset.id),
          goal_id: parseInt(evt.item.dataset.parent_id)
        }
      }
    })
  }

  let openNewTaskModal = $state(false)
  let newTaskComp: NewTask = $state()

  let savingTask = false
  async function createTask(): Promise<void> {
    savingTask = true
    try {
      await xhr.post(`/plans/${goal.plan_id}/tasks?channel=${channel_id}`, {})
    } catch {
      toast("There was an error creating the task", "error")
    } finally {
      savingTask = false
    }
  }

  onMount(() => {
    setTimeout(() => {
      setSortable()
    }, 200)
  })
</script>

<div
  class="border-l-4 border-quarterly-goal shadow-sm relative bg-white quarterly-goal select-none"
  class:bg-blue-50={goal._selected}
  id="goal-{goal.id}"
  data-goal_id={goal.id}
  data-name="quarterly-goal"
  ondblclick={() => onopenGoalModal(goal)}
  role="button"
  tabindex="0"
>
  {#if appStore.tasks.filter((t) => t.goal_id === goal.id).length}
    <div class="absolute top-10" style="left:-14px">
      <Expanded
        class="text-quarterly-goal"
        bind:expanded={expand}
        data={{ name: `goal-${goal.id}-expander` }}
      />
    </div>
  {/if}
  <!--Select tasks-->

  {#if period === "prev" && appStore.tasks.filter((t) => t.goal_id === goal.id).length > 0}
    <div class="absolute bottom-1 left-2">
      <button
        onclick={(e) => {
          e.preventDefault()
          setToggleTasks()
        }}
        class="cursor-pointer flex text-xs items-center text-blue-700"
        title="{toggleTasks ? 'Unselect' : 'Select'} all tasks"
      >
        {#if toggleTasks}
          <SquareCheck class="text-blue-600 size-4 mr-0.5" /> Unselect all tasks
        {:else}
          <Square class="text-blue-600 size-4 mr-0.5" /> Select all tasks
        {/if}
      </button>
    </div>
  {/if}

  <!--Select checkbox-->

  {#if allowGoalSelect}
    <div class="absolute top-1 left-2">
      <input
        type="checkbox"
        checked={goal._selected}
        onclick={updateGoalSelected}
      />
    </div>
  {/if}

  <!--button to move goals to other parent-->
  {#if showMoveToParent}
    <button
      class="rounded text-xs px-2 absolute bottom-1 right-1 bg-blue-200 hover:bg-blue-600 text-white"
      onclick={moveToParent}
    >
      Move to parent
    </button>
  {/if}

  <div
    class="pt-2 pb-4 px-2 border-gray-300 border-b border-t flex items-center"
  >
    <div
      class="flex flex-col w-full ml-4 items-center"
      ondblclick={() => onopenGoalModal(goal)}
      role="button"
      tabindex="0"
    >
      <div class="flex w-full ml-6 items-center">
        <div class="w-10/12 text-left flex items-center">
          <span class="text-gray-500 text-xs mr-1"
            >{pos > 0 ? `${pos}.` : ""}</span
          >
          <div class="w-full">
            <InlineInput
              onsuccess={(d: GoalType) => updateGoal(d)}
              bind:value={goal.name}
              postURL={`/plans/${goal.plan_id}/goals/${goal.id}.json?channel=${channel_id}`}
              name="name"
              model="goal"
            />
          </div>
        </div>

        <div class="w-2/12 text-center"></div>
      </div>
      <div class="flex w-full ml-6 py-2 items-center">
        <div class="w-6/12 flex items-center justify-start">
          <EditGoalStatus
            goalOrTask={goal}
            type="Goal"
            small={true}
            show_border={false}
            editable={true}
            onupdate={(data) => updateGoal(data)}
          />
        </div>
        <div class="w-6/12 flex items-center justify-center">
          {formatDate(goal.expected_at)}
        </div>
      </div>
    </div>
  </div>

  <div class="absolute top-1 right-1">
    <Dropdown bind:open={openGoalDropdown}>
      {#snippet button()}
        <span
          class="rounded-full flex items-center justify-center focus:outline-none"
          data-name="goal-dropdown"
        >
          <CircleEllipsis class="text-gray-400 w-7 h-7" />
        </span>
      {/snippet}

      <div>
        <div
          class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
          role="menu"
        >
          <div class="py-1" role="none">
            <button
              onclick={() => {
                openGoalDropdown = false
                onopenGoalModal(goal)
              }}
              type="button"
              class="btn-dropdown"
              role="menuitem"
              data-name="goal-details-button"
            >
              <Info class="text-gray-400 w-4 h-4 mr-3" />
              See Details
            </button>

            {#if period === "prev" && allowDuplicate}
              <button
                type="button"
                class="btn-dropdown"
                onclick={() => {
                  openGoalDropdown = false
                  openDuplicateGoal = true
                }}
                data-name="goal-duplicate-button"
              >
                <Copy class="text-gray-400 w-4 h-4 mr-3" />
                Duplicate to&nbsp;
                <span class="font-semibold">{dates.next_quarter}</span>
              </button>
            {/if}
            <button
              type="button"
              class="btn-dropdown"
              onclick={() => (openNewTaskModal = true)}
              data-name="goal-subtask-button"
            >
              <SquarePlus class="text-gray-400 w-4 h-4 mr-3" />
              Add Sub task
            </button>

            <button
              class="btn-dropdown"
              onclick={() => {
                openGoalDropdown = false
                deleteGoal()
              }}
              data-name="goal-delete-button"
            >
              <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </Dropdown>
  </div>
</div>

<div class="pl-2" class:hidden={!expand} bind:this={sortableSection}>
  {#each appStore.tasks
    .filter((t) => t.goal_id === goal.id)
    .sort((a, b) => a.parent_position - b.parent_position) as task, idx (task.id)}
    <Task
      {task}
      {goal}
      allowSelect={allowTaskSelect}
      pos={idx + 1}
      {onopenTaskModal}
    />
  {/each}
</div>

<div>
  {#if allowDuplicate}
    <Modal bind:open={openDuplicateGoal}>
      <QuarterlyDuplicate
        {goal}
        onduplicated={(g) => {
          toast("Goal duplicated successfully", "success")
          openDuplicateGoal = false
        }}
      />
    </Modal>
  {/if}
</div>

<Modal
  open={openNewTaskModal}
  closeConfirm={true}
  onclose={() => {
    if (newTaskComp?.changesCount() >= 1) {
      if (
        confirm(
          "Are you sure you want to close the modal? You will lose all your changes!"
        )
      ) {
        openNewTaskModal = false
      }
    } else {
      openNewTaskModal = false
    }
  }}
>
  <NewTask
    {goal}
    oncreate={() => (openNewTaskModal = false)}
    bind:this={newTaskComp}
  />
</Modal>
